import { useMemo } from 'react';
import type { DocDocument, DocTemplate, LibraryScore } from '../types/docops';
import { PIPELINE_STAGES } from '../components/admin/documents/docopsConstants';
import { COMPLETED_STATUSES, UNGENERATED_STATUSES, getDepImportance } from '../utils/docopsPipeline';

interface UsePipelineDataParams {
  documents: DocDocument[];
  templates: DocTemplate[];
  libraryScore: LibraryScore | null;
  jobDocIds?: Set<string>;
}

export function usePipelineData({ documents, templates, libraryScore, jobDocIds }: UsePipelineDataParams) {
  const totalDocs = documents.length;

  const blockerDocs = useMemo(() =>
    documents.filter(d =>
      d.launchBlocker && !COMPLETED_STATUSES.has(d.status),
    ), [documents]);

  const attorneyDocs = useMemo(() =>
    documents.filter(d => d.status === 'attorney_review'), [documents]);

  const generatedDocs = useMemo(() =>
    documents.filter(d => !UNGENERATED_STATUSES.has(d.status)), [documents]);

  const pipelineGroups = useMemo(() => {
    const getSortOrder = (doc: DocDocument): number => {
      const tpl = templates.find(t => t.id === doc.templateId);
      return tpl ? tpl.sortOrder : 999;
    };

    const getDepImportanceLocal = (doc: DocDocument): number =>
      getDepImportance(doc, templates, libraryScore?.conflicts ?? []);

    const groups: Record<string, DocDocument[]> = {};
    for (const stage of PIPELINE_STAGES) {
      groups[stage.id] = [];
    }
    for (const doc of documents) {
      // If this doc is part of an active generation job and still 'not_started',
      // show it in the Generating stage instead of Needs Generation
      if (jobDocIds?.has(doc.templateId) && doc.status === 'not_started') {
        groups['in_progress'].push(doc);
      } else {
        const stage = PIPELINE_STAGES.find(s => s.statuses.includes(doc.status));
        if (stage) groups[stage.id].push(doc);
      }
    }
    for (const key of Object.keys(groups)) {
      if (key === 'needs_generation') {
        groups[key].sort((a, b) => getDepImportanceLocal(b) - getDepImportanceLocal(a));
      } else {
        groups[key].sort((a, b) => getSortOrder(a) - getSortOrder(b));
      }
    }
    return groups;
  }, [documents, templates, libraryScore, jobDocIds]);

  const nextRoundInfo = useMemo(() => {
    const notStarted = documents.filter(d => d.status === 'not_started');
    if (notStarted.length === 0) return null;

    const roundMap: Record<number, DocDocument[]> = {};
    for (const doc of notStarted) {
      const tpl = templates.find(t => t.id === doc.templateId);
      const round = tpl?.generationRound ?? 6;
      if (!roundMap[round]) roundMap[round] = [];
      roundMap[round].push(doc);
    }

    const rounds = Object.keys(roundMap).map(Number).sort((a, b) => a - b);
    if (rounds.length === 0) return null;

    const nextRound = rounds[0];
    const docs = roundMap[nextRound];
    const totalRounds = Math.max(...templates.map(t => t.generationRound ?? 1));
    const completedRounds = Array.from({ length: totalRounds }, (_, i) => i + 1)
      .filter(r => {
        const roundDocs = documents.filter(d => {
          const t = templates.find(x => x.id === d.templateId);
          return (t?.generationRound ?? 6) === r;
        });
        return roundDocs.length > 0 && roundDocs.every(d => d.status !== 'not_started');
      }).length;

    return { round: nextRound, docs, count: docs.length, totalRounds, completedRounds };
  }, [documents, templates]);

  return { totalDocs, blockerDocs, attorneyDocs, generatedDocs, pipelineGroups, nextRoundInfo };
}
